import { useEffect } from 'react';
import { Drawer, createDrawer } from '@nexelem/drawer';
import { DetailsLoadingSkeleton } from '../../../../../components/molecules/DetailsLoadingSkeleton';
import { TaskStatus } from '@nexelem/graphne-api';
import { DrawerEmptyState } from './components/DrawerEmptyState';
import { openRemoveTaskFromPlanModal } from './components/RemoveFromPlanModal';
import { TaskDetailsDrawerContent } from './components/TaskDetailsDrawerContent';
import { ExternalId, TaskDetailsDrawerFooter } from './components/TaskDetailsDrawerFooter';
import { TaskDetailsDrawerHeader } from './components/TaskDetailsDrawerHeader';
import { useGraphneTaskDetails } from '../../../../../queryHooks/productionPlanHooks';
import { useAvailableQueues } from '../../../../../queryHooks/useAvailableQueues';
import { openAddTaskToPlanModal } from './components/AddToPlanModal';
import { useProductionPlanSearchStore } from '../../../../../components/organisms/ProductionPlans/store';
import { PlanView, useProductionPlanQueryParams } from '../../../../../components/organisms/ProductionPlans/hooks';

type TaskDetailsDrawerProps = {
  taskId: string;
  withBackToTaskDrawerButton?: boolean;
  isGraphDrawer?: boolean;
};

const TaskDetailsDrawer = ({
  taskId,
  withBackToTaskDrawerButton = false,
  isGraphDrawer = false,
}: TaskDetailsDrawerProps) => {
  // TODO: Assign Worker - waiting for BE
  // TODO: Source - waiting for Zosia & BE
  const { data: task, isLoading: taskLoading } = useGraphneTaskDetails(taskId);
  const { data: availableQueuesData, isLoading: availableQueuesLoading } = useAvailableQueues(taskId);
  const setSearchResults = useProductionPlanSearchStore((state) => state.setSearchResults);
  const { queryParams } = useProductionPlanQueryParams();

  const isCurrentlyKanban = queryParams.view === PlanView.KANBAN;
  const isLoading = taskLoading || availableQueuesLoading;
  const isSuggested = task?.queue?.id === availableQueuesData?.suggestedQueueId;
  const isTaskNotPlanned = task?.status === TaskStatus.NotPlanned || false;
  const isFooterDisplayed = isGraphDrawer ? isTaskNotPlanned : true;

  useEffect(
    () => () => {
      setSearchResults([]);
    },
    [setSearchResults],
  );

  if (isLoading) {
    return (
      <Drawer>
        <DetailsLoadingSkeleton amount={3} className="p-6" />
      </Drawer>
    );
  }

  if (!task) {
    return <DrawerEmptyState />;
  }

  const handleAddToPlan = () => {
    openAddTaskToPlanModal({
      task,
    });
  };

  const handleRemoveFromPlan = () => {
    openRemoveTaskFromPlanModal({
      task,
    });
  };

  return (
    <Drawer>
      <TaskDetailsDrawerHeader hasBackButton={withBackToTaskDrawerButton} task={task} />
      <TaskDetailsDrawerContent
        isGraphDrawer={isGraphDrawer}
        isKanban={isCurrentlyKanban}
        isSuggested={isSuggested}
        task={task}
      />
      {isFooterDisplayed && (
        <TaskDetailsDrawerFooter
          externalId={task.externalId as ExternalId}
          isGraphDrawer={isGraphDrawer}
          isKanban={isCurrentlyKanban}
          isTaskNotPlanned={isTaskNotPlanned}
          queueId={task.queue?.id}
          taskId={task.id}
          taskStartDate={task.start}
          taskStatus={task.status}
          onModalToggle={handleAddToPlan}
          onRemoveModalToggle={handleRemoveFromPlan}
        />
      )}
    </Drawer>
  );
};

export const { toggleTaskDetailsDrawer, closeTaskDetailsDrawer, useIsOpenTaskDetailsDrawer, openTaskDetailsDrawer } =
  createDrawer(TaskDetailsDrawer, {
    name: 'TaskDetailsDrawer',
    slot: 'left',
    width: 420,
    drawerId: (props) => props.taskId,
  });
